import React from 'react';
import { Mail, Phone, Calendar, Briefcase, UserMinus } from 'lucide-react';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { BRAND_COLORS, SYSTEM_COLORS, NEUTRAL_COLORS } from '../../../constants/brandPalette';
import backgroundImg from '../../../assets/images/background.jpg';

// ── Helpers ───────────────────────────────────────────────────────────────────

const ROLE_STYLES = {
  admin:            { bg: '#fee2e2', color: '#b91c1c' },
  supervisor:       { bg: '#fef3c7', color: '#b45309' },
  hod:              { bg: '#fdf4ff', color: '#7e22ce' },
  leader:           { bg: '#bfdbfe', color: '#1d4ed8' },
  developer:        { bg: '#dcfce7', color: '#15803d' },
  community_member: { bg: '#e0f2fe', color: '#0369a1' },
  resource_manager: { bg: '#fff7ed', color: '#c2410c' },
  graphics:         { bg: '#fdf2f8', color: '#9d174d' },
  marketing:        { bg: '#ecfdf5', color: '#065f46' },
  pr:               { bg: '#f0fdf4', color: '#166534' },
  client:           { bg: '#f1f5f9', color: '#475569' },
};

// Each entry is [fromColor, toColor] for the banner gradient
const BANNER_GRADIENTS = [
  ['#37559e', '#06b6d4'],
  ['#7c3aed', '#ec4899'],
  ['#0f766e', '#3b82f6'],
  ['#be185d', '#f59e0b'],
  ['#1d4ed8', '#8b5cf6'],
  ['#b45309', '#ef4444'],
  ['#065f46', '#10b981'],
  ['#9d174d', '#f97316'],
  ['#1e40af', '#06b6d4'],
  ['#6d28d9', '#f43f5e'],
  ['#0e7490', '#22c55e'],
  ['#92400e', '#eab308'],
  ['#7e22ce', '#3b82f6'],
  ['#166534', '#14b8a6'],
  ['#dc2626', '#f59e0b'],
  ['#0369a1', '#8b5cf6'],
];

const nameHash = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return Math.abs(h);
};

const avatarColor = (name = '') => {
  const [from] = BANNER_GRADIENTS[nameHash(name) % BANNER_GRADIENTS.length];
  return from;
};

const getInitials = (name = '') =>
  name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'U';

const formatRole = (role = '') =>
  role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const fmtDate = (ts) => {
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch { return null; }
};

// ── TeamMemberCard ────────────────────────────────────────────────────────────

const TeamMemberCard = ({ member, onDelete, onAssignTask, onViewProfile }) => {
  const { hasPermission, hasRole } = useApiAuth();
  const canManageUsers = hasPermission('users.delete') || hasRole('admin');
  const canAssignTasks =
    hasPermission('tasks.assign') ||
    hasRole(['admin', 'leader', 'supervisor', 'hod']);

  const rs = ROLE_STYLES[member.role] || { bg: '#f3f4f6', color: '#4b5563' };
  const h  = nameHash(member.name || '');
  const [gradFrom, gradTo] = BANNER_GRADIENTS[h % BANNER_GRADIENTS.length];
  const color = gradFrom;

  const photoSrc = member.photo_url || member.profileImage || null;
  const joinedAt = fmtDate(member.createdAt || member.created_at);

  const skills = Array.isArray(member.skills) ? member.skills : [];
  const visibleSkills = skills.slice(0, 4);
  const extraSkills   = skills.length - 4;

  return (
    <div
      className="flex flex-col rounded-2xl border overflow-hidden transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
      style={{
        backgroundColor: SYSTEM_COLORS.shellSurface,
        borderColor: SYSTEM_COLORS.borderSoft,
        boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
      }}
    >
      {/* ── Banner ─────────────────────────────────────────────────────────── */}
      <div className="relative flex-shrink-0" style={{ height: 96 }}>
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(180deg, ${gradFrom}99 0%, ${gradTo}77 100%), url(${backgroundImg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        {/* Action buttons — top right */}
        <div className="absolute top-2.5 right-2.5 flex gap-1.5 z-10">
          {canAssignTasks && typeof onAssignTask === 'function' && (
            <button
              onClick={() => onAssignTask(member.id)}
              title="Assign Task"
              className="flex items-center justify-center rounded-lg backdrop-blur-sm transition-opacity hover:opacity-80"
              style={{
                width: 28, height: 28,
                backgroundColor: 'rgba(255,255,255,0.25)',
                color: '#fff',
              }}
            >
              <Briefcase size={13} />
            </button>
          )}
          {canManageUsers && typeof onDelete === 'function' && (
            <button
              onClick={() => onDelete(member.id)}
              title="Make Dormant"
              className="flex items-center justify-center rounded-lg backdrop-blur-sm transition-opacity hover:opacity-80"
              style={{
                width: 28, height: 28,
                backgroundColor: 'rgba(255,255,255,0.25)',
                color: '#fff',
              }}
            >
              <UserMinus size={13} />
            </button>
          )}
        </div>

        {/* Avatar — centered, half overlapping banner */}
        <div
          className="absolute left-1/2 -translate-x-1/2 z-10"
          style={{ bottom: -32 }}
        >
          <div
            className="rounded-full overflow-hidden flex items-center justify-center text-white font-bold"
            style={{
              width: 64, height: 64,
              fontSize: 22,
              backgroundColor: color,
              border: '3px solid #ffffff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            {photoSrc
              ? <img src={photoSrc} alt={member.name} className="w-full h-full object-cover" />
              : getInitials(member.name)
            }
          </div>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 px-5 pt-10 pb-4 flex-1">

        {/* Name + role badge */}
        <div className="text-center">
          <p className="text-sm font-bold" style={{ color: NEUTRAL_COLORS.black }}>
            {member.name || 'Unknown'}
          </p>
          <span
            className="inline-block mt-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold"
            style={{ backgroundColor: rs.bg, color: rs.color }}
          >
            {formatRole(member.role)}
          </span>
        </div>

        {/* Bio */}
        {member.bio && (
          <p
            className="text-xs text-center line-clamp-2 leading-relaxed"
            style={{ color: SYSTEM_COLORS.textMuted }}
          >
            {member.bio}
          </p>
        )}

        {/* Contact details */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-xs truncate" style={{ color: SYSTEM_COLORS.textMuted }}>
            <Mail size={11} className="flex-shrink-0" style={{ color: BRAND_COLORS.primary }} />
            <span className="truncate">{member.email}</span>
          </div>
          {member.phone && (
            <div className="flex items-center gap-2 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
              <Phone size={11} className="flex-shrink-0" style={{ color: BRAND_COLORS.primary }} />
              <span>{member.phone}</span>
            </div>
          )}
        </div>

        {/* Skills */}
        {skills.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {visibleSkills.map((skill, i) => {
              const label = typeof skill === 'string' ? skill : skill.name;
              return (
                <span
                  key={i}
                  className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                  style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}
                >
                  {label}
                </span>
              );
            })}
            {extraSkills > 0 && (
              <span
                className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                style={{ backgroundColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted }}
              >
                +{extraSkills}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-5 py-3"
        style={{ borderTop: `1px solid ${SYSTEM_COLORS.borderSoft}` }}
      >
        <div className="flex items-center gap-1.5 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
          <Calendar size={11} style={{ color: BRAND_COLORS.primary }} />
          <span>{joinedAt ? `Joined ${joinedAt}` : 'Joining date unknown'}</span>
        </div>

        {typeof onViewProfile === 'function' && (
          <button
            type="button"
            onClick={() => onViewProfile(member.id)}
            className="text-[10px] font-semibold px-2.5 py-1 rounded-lg transition-opacity hover:opacity-80"
            style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}
          >
            View
          </button>
        )}
      </div>
    </div>
  );
};

export default TeamMemberCard;
